import React, { useMemo } from 'react';
import ManCrossPageFooter from '../components/ManCrossPageFooter';
import { computeIvcLevel, formatRounded, toDimensionRows } from '../shared/managerialUtils';

// ── Color palette ──────────────────────────────────────────────────────────────
const C = {
    critical:  '#F3202A',   // IVC≥2 / IPRA<65 / IPRA dimension
    high:      '#F48732',   // IVC=1  / IPRA 65-74 / IASH dimension
    neutral:   '#CBD5E0',   // no risk / others
    criticalBg: '#FDE6E8',
    highBg:     '#FEF0E6',
    neutralBg:  '#EEF1F5',
    white:     '#ffffff',
};

// IVC risk score factor for area Pareto weighting
const AREA_RISK_FACTOR = { 0: 1, 1: 2, 2: 4 };

// ── Sub-components ─────────────────────────────────────────────────────────────

/** Single horizontal bar row */
const ParetoBar = ({ name, fullName, barPct, colorKey, rightEl }) => {
    const fillColor = colorKey === 'critical' ? C.critical
                    : colorKey === 'high'     ? C.high
                    : C.neutral;
    const trackBg   = colorKey === 'none' || colorKey === 'neutral' ? C.neutralBg
                    : colorKey === 'critical' ? C.criticalBg
                    : C.highBg;
    return (
        <div className="mgcr-pt-bar-row">
            <span className="mgcr-pt-bar-name" title={fullName || name}>{name}</span>
            <div className="mgcr-pt-bar-track" style={{ background: trackBg }}>
                <div
                    className="mgcr-pt-bar-fill"
                    style={{ width: `${Math.max(barPct, 2)}%`, background: fillColor }}
                />
            </div>
            <div className="mgcr-pt-bar-right">{rightEl}</div>
        </div>
    );
};

/** Pareto 80% threshold separator */
const ParetoMarker = () => (
    <div className="mgcr-pt-marker">
        <div className="mgcr-pt-marker-line" />
        <span className="mgcr-pt-marker-badge">80%</span>
    </div>
);

/** Colored badge pill (cumulative %, brecha, etc.) */
const BadgePill = ({ value, colorKey }) => {
    const bg   = colorKey === 'critical' ? C.critical
               : colorKey === 'high'     ? C.high
               : C.neutral;
    const text = colorKey === 'high' ? '#1a1a1a' : C.white;
    return (
        <span className="mgcr-pt-badge-pill" style={{ background: bg, color: text }}>
            {value}
        </span>
    );
};

// ── Data builders ──────────────────────────────────────────────────────────────

function buildAreaItems(iashTotals, ipraTotals, headcountMap) {
    const iashMap  = {};
    (iashTotals?.average_by_management_area || []).forEach(a => { iashMap[a.name]  = a.average; });
    const ipraMap  = {};
    (ipraTotals?.average_by_management_area || []).forEach(a => { ipraMap[a.name] = a.average; });

    const names = new Set([...Object.keys(iashMap), ...Object.keys(ipraMap)]);
    const items = [...names].map(name => {
        const hc       = headcountMap[name] || 0;
        const iash     = iashMap[name]  ?? null;
        const ipra     = ipraMap[name]  ?? null;
        const ivcLevel = computeIvcLevel(ipra, iash);
        const factor   = AREA_RISK_FACTOR[ivcLevel] ?? 1;
        return { name, headcount: hc, ivcLevel, score: hc * factor };
    });

    items.sort((a, b) => b.ivcLevel - a.ivcLevel || b.headcount - a.headcount);

    const maxScore   = Math.max(1, ...items.map(i => i.score));
    const totalScore = items.reduce((s, i) => s + i.score, 0);
    const totalHC    = items.reduce((s, i) => s + i.headcount, 0);

    let cumScore = 0;
    const rows = items.map(item => {
        cumScore += item.score;
        const cumPct   = totalScore > 0 ? Math.round(cumScore / totalScore * 100) : 100;
        const hcPct    = totalHC    > 0 ? Math.round(item.headcount / totalHC * 100) : 0;
        const colorKey = item.ivcLevel >= 2 ? 'critical' : item.ivcLevel === 1 ? 'high' : 'neutral';
        return {
            ...item,
            barPct:   Math.round(item.score / maxScore * 100),
            cumPct,
            hcPct,
            colorKey,
        };
    });

    // Pareto cutoff: first index where cumulative score ≥ 80%
    let cumS = 0;
    let cutoffIdx = rows.length - 1;
    for (let i = 0; i < rows.length; i++) {
        cumS += rows[i].score;
        if (totalScore > 0 && cumS / totalScore >= 0.80) { cutoffIdx = i; break; }
    }
    const nCritical = cutoffIdx + 1;

    // Dato maestro values
    const topHCPct = Math.round(
        rows.slice(0, nCritical).reduce((s, r) => s + r.headcount, 0) / Math.max(1, totalHC) * 100
    );
    const top2 = rows.slice(0, 2).map(r => `${r.name} (${r.hcPct}%)`).join(' y ');

    return { rows, cutoffIdx, nCritical, topHCPct, top2, topNames: rows.slice(0, nCritical).map(r => r.name) };
}

function buildSupItems(criticalSups, totalSupCount) {
    const THRESHOLD = 75;
    const named = criticalSups.map(s => {
        const score = Math.max(0, THRESHOLD - s.ipra_score);
        const colorKey = s.ipra_score < 65 ? 'critical' : 'high';
        // Usar code (SUP-XX) como label; el nombre real se guarda en fullName para tooltip
        return { code: s.code, name: s.code, fullName: s.name, ipraScore: s.ipra_score, score, colorKey };
    });

    const totalNamedScore = Math.max(1, named.reduce((s, i) => s + i.score, 0));
    const maxScore        = Math.max(1, ...named.map(i => i.score));

    let cumScore = 0;
    const rows = named.map(s => {
        cumScore += s.score;
        return {
            ...s,
            barPct:       Math.round(s.score / maxScore * 100),
            cumPct:       Math.round(cumScore / totalNamedScore * 100),
            indivPct:     Math.round(s.score  / totalNamedScore * 100),
        };
    });

    // Others row
    const othersCount = Math.max(0, (totalSupCount || 0) - named.length);
    if (othersCount > 0) {
        const smallBarPct = Math.max(5, Math.round(named[named.length - 1]?.barPct * 0.4 || 8));
        rows.push({ code: 'OTROS', name: `Otros ${othersCount}`, ipraScore: null, score: 0, colorKey: 'none', barPct: smallBarPct, cumPct: 100, indivPct: null });
    }

    // Pareto cutoff = last supervisor with IPRA < 65
    const cutoffIdx  = named.filter(s => s.colorKey === 'critical').length - 1;
    const nCritical  = Math.max(1, cutoffIdx + 1);
    const cumAtCutoff = rows[cutoffIdx] ? rows[cutoffIdx].cumPct : 0;

    const top2 = named.slice(0, 2).map(r => `${r.code} (${r.indivPct}%)`).join(' y ');

    return { rows, cutoffIdx, nCritical, cumAtCutoff, top2 };
}

function buildDimItems(iashTotals, ipraTotals) {
    const THRESHOLD = 75;

    const iashRows = toDimensionRows(iashTotals?.average_by_dimension)
        .filter(r => r.average != null)
        .map(r => ({
            ...r,
            instrument: 'IASH',
            score:  (r.weightPercent ?? 10) / 100 * Math.max(0, THRESHOLD - r.average),
            brecha: Math.round(r.average - THRESHOLD),
        }));

    const ipraRows = toDimensionRows(ipraTotals?.average_by_dimension)
        .filter(r => r.average != null)
        .map(r => ({
            ...r,
            instrument: 'IPRA',
            score:  (r.weightPercent ?? 10) / 100 * Math.max(0, THRESHOLD - r.average),
            brecha: Math.round(r.average - THRESHOLD),
        }));

    const all = [...ipraRows, ...iashRows]
        .filter(r => r.score > 0)
        .sort((a, b) => b.score - a.score);

    const TOP_N    = 6;
    const top      = all.slice(0, TOP_N);
    const rest     = all.slice(TOP_N);
    const maxScore = Math.max(1, ...top.map(i => i.score));
    const totalScore = all.reduce((s, i) => s + i.score, 0);

    let cumScore = 0;
    const rows = top.map(r => {
        cumScore += r.score;
        return {
            ...r,
            barPct:    Math.round(r.score / maxScore * 100),
            cumPct:    totalScore > 0 ? Math.round(cumScore / totalScore * 100) : 100,
            colorKey:  r.instrument === 'IPRA' ? 'critical' : 'high',
        };
    });

    if (rest.length > 0) {
        const restScore = rest.reduce((s, i) => s + i.score, 0);
        rows.push({
            dimension:  `Otras ${rest.length}`,
            instrument: 'NONE',
            score:       restScore,
            brecha:      Math.round(rest.reduce((s, r) => s + r.brecha, 0) / rest.length),
            barPct:      Math.max(5, Math.round(restScore / maxScore * 100)),
            cumPct:      100,
            colorKey:   'neutral',
        });
    }

    // Pareto cutoff (80% of total weighted deficit)
    let cumS = 0;
    let cutoffIdx = rows.filter(r => r.instrument !== 'NONE').length - 1;
    for (let i = 0; i < rows.length; i++) {
        if (rows[i].instrument === 'NONE') continue;
        cumS += rows[i].score;
        if (totalScore > 0 && cumS / totalScore >= 0.80) { cutoffIdx = i; break; }
    }
    const nCritical  = cutoffIdx + 1;
    const cumAtCutoff = rows[cutoffIdx] ? rows[cutoffIdx].cumPct : 0;
    const top2Names  = top.slice(0, 2).map(r => r.dimension).join(' y ');

    return { rows, cutoffIdx, nCritical, cumAtCutoff, top2Names };
}

// ── Panel component ────────────────────────────────────────────────────────────

const ParetoPanel = ({ count, countUnit, title, rows, cutoffIdx, dataMaestro, detalle }) => (
    <div className="mgcr-pt-panel">
        {/* Panel title */}
        <p className="mgcr-pt-panel-title">{title}</p>

        {/* Bar chart */}
        <div className="mgcr-pt-bars">
            {rows.map((row, i) => (
                <React.Fragment key={row.code || row.dimension || row.name || i}>
                    {/* Pareto 80% marker after cutoff row (only when cutoff is valid) */}
                    {cutoffIdx >= 0 && i === cutoffIdx + 1 && <ParetoMarker />}
                    <ParetoBar
                        name={row.name || row.dimension}
                        fullName={row.fullName}
                        barPct={row.barPct}
                        colorKey={row.colorKey}
                        rightEl={row._rightEl}
                    />
                </React.Fragment>
            ))}
        </div>

        {/* Data maestro notes */}
        <div className="mgcr-pt-notes">
            <p className="mgcr-pt-note">
                <strong>Dato Maestro:</strong>{' '}{dataMaestro}
            </p>
            <p className="mgcr-pt-note">
                <strong>Detalle:</strong>{' '}{detalle}
            </p>
        </div>
    </div>
);

// ── Main component ─────────────────────────────────────────────────────────────

const ManCrossPagePareto = ({ reportData, headerText, pageNumber }) => {
    const payload      = reportData?.payload || {};
    const iashTotals   = payload?.totals?.iash || {};
    const ipraTotals   = payload?.totals?.ipra || {};
    const criticalSups = payload?.critical_supervisors || [];
    const totalSupCount= payload?.total_supervisor_count ?? 0;

    const headcountMap = useMemo(() => {
        const map = {};
        (payload?.headcount_by_management_area || []).forEach(e => { map[e.name] = e.headcount; });
        return map;
    }, [payload]);

    // ── Build area data ──
    const areaData = useMemo(
        () => buildAreaItems(iashTotals, ipraTotals, headcountMap),
        [iashTotals, ipraTotals, headcountMap]
    );

    // Inject right element (N Pers.) into area rows
    const areaRows = useMemo(
        () => areaData.rows.map(r => ({
            ...r,
            _rightEl: <span className="mgcr-pt-hc-label">{r.headcount} Pers.</span>,
        })),
        [areaData]
    );

    // ── Build supervisor data ──
    const supData = useMemo(
        () => buildSupItems(criticalSups, totalSupCount),
        [criticalSups, totalSupCount]
    );

    const supRows = useMemo(
        () => supData.rows.map(r => ({
            ...r,
            _rightEl: r.cumPct != null
                ? <BadgePill value={`${r.cumPct}%`} colorKey={r.colorKey} />
                : null,
        })),
        [supData]
    );

    // ── Build dimension data ──
    const dimData = useMemo(
        () => buildDimItems(iashTotals, ipraTotals),
        [iashTotals, ipraTotals]
    );

    const dimRows = useMemo(
        () => dimData.rows.map(r => ({
            ...r,
            name: r.dimension,
            _rightEl: r.brecha != null
                ? <BadgePill
                    value={r.brecha > 0 ? `+${r.brecha}` : String(r.brecha)}
                    colorKey={r.colorKey}
                  />
                : null,
        })),
        [dimData]
    );

    // ── Dynamic headline ──
    const n1 = areaData.nCritical;
    const n2 = supData.nCritical;
    const n3 = dimData.nCritical;
    const area1 = areaData.topNames?.[0] || 'Área 1';
    const area2 = areaData.topNames?.[1] ? ` y ${areaData.topNames[1]}` : '';

    // ── Footer text ──
    const footerText = `Enfocar el 100% del esfuerzo en ${area1}${area2}, ${n2} líder${n2 !== 1 ? 'es' : ''} y ${n3} dimensión${n3 !== 1 ? 'es' : ''} resuelve el 80% del problema de seguridad de la planta.`;

    return (
        <div className="mgcr-pt-page">

            {/* ── Page header ── */}
            <div className="mgcr-page-header">
                <span className="mgcr-page-header-text">{headerText}</span>
                <span className="mgcr-page-header-num">{pageNumber}</span>
            </div>

            <div className="keep-blankspace" />

            {/* ── Title area ── */}
            <div className="mgcr-pt-title-area">
                <h2 className="mgcr-pt-main-title">Foco Estratégico</h2>
                <p  className="mgcr-pt-subtitle">Pareto de Riesgo</p>
            </div>

            {/* ── Headline bullet ── */}
            <div className="mgcr-pt-headline">
                <span className="mgcr-pt-headline-bullet">•</span>
                <span>
                    El 80% del riesgo total se concentra en{' '}
                    <strong>{n1} área{n1 !== 1 ? 's' : ''}</strong>,{' '}
                    <strong>{n2} supervisor{n2 !== 1 ? 'es' : ''}</strong>{' '}
                    y{' '}
                    <strong>{n3} dimensión{n3 !== 1 ? 'es' : ''}</strong>
                </span>
            </div>

            {/* ── Three panels ── */}
            <div className="mgcr-pt-panels">

                {/* Panel 1 — Áreas */}
                <ParetoPanel
                    count={n1}
                    countUnit="Áreas"
                    title="Áreas Críticas"
                    rows={areaRows}
                    cutoffIdx={areaData.cutoffIdx}
                    dataMaestro={`${areaData.topHCPct}% del riesgo concentrado en estas ${n1} unidad${n1 !== 1 ? 'es' : ''}.`}
                    detalle={areaData.top2}
                />

                {/* Panel 2 — Supervisores */}
                <ParetoPanel
                    count={n2}
                    countUnit="Supervisores"
                    title="Líderes Clave"
                    rows={supRows}
                    cutoffIdx={supData.cutoffIdx}
                    dataMaestro={`${n2} supervisor${n2 !== 1 ? 'es' : ''} = ${supData.cumAtCutoff}% de exposición total.`}
                    detalle={`Enfoque total en ${supData.top2}.`}
                />

                {/* Panel 3 — Dimensiones */}
                <ParetoPanel
                    count={n3}
                    countUnit="Dimensiones"
                    title="Dimensiones Prioritarias"
                    rows={dimRows}
                    cutoffIdx={dimData.cutoffIdx}
                    dataMaestro={`${n3} dimensión${n3 !== 1 ? 'es' : ''} = ${dimData.cumAtCutoff}% del déficit.`}
                    detalle={dimData.top2Names}
                />

            </div>

            {/* ── Footer implication ── */}
            <div className="mgcr-insight-footer">
                <strong>Esto implica que:</strong>{' '}{footerText}
            </div>

            <div className="keep-blankspace" />

            <ManCrossPageFooter />
        </div>
    );
};

export default ManCrossPagePareto;
